import { useState, useCallback, useMemo, useEffect } from "react";
import { HeroSection } from "./HeroSection";
import { NewsCard } from "./NewsCard";
import { SearchBar } from "./SearchBar";
import { FilterChips } from "./FilterChips";
import { ArticleDrawer } from "./ArticleDrawer";
import { TagBadge } from "./TagBadge";
import { useItems } from "../hooks/useItems";
import { useTopStories } from "../hooks/useTopStories";
import { useSources } from "../hooks/useSources";
import { useStats } from "../hooks/useStats";
import { getAuthorDomain, getFaviconUrl } from "../utils/tagColors";
import { resolveMediaUrl } from "../api/client";
import type { ItemListEntry } from "../types";
import type { FilterConfig } from "./SettingsView";

interface NewsDashboardProps {
  activeTag?: string;
  onTagChange: (tag: string | undefined) => void;
  filterConfig?: FilterConfig;
}

export function NewsDashboard({ activeTag, onTagChange, filterConfig }: NewsDashboardProps) {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [highRelevance, setHighRelevance] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { items: topStories, refresh: refreshTop } = useTopStories(80, 1);
  const { sources } = useSources();
  const { stats } = useStats(60000);

  // Source-Lookup-Map: sourceId → { logo_url, type }
  const sourcesMap = useMemo(() => {
    const map = new Map<number, { logo_url: string | null; type: string }>();
    for (const s of sources) {
      map.set(s.id, { logo_url: s.logo_url, type: s.type });
    }
    return map;
  }, [sources]);

  const getItemLogo = useCallback((item: ItemListEntry): string | null => {
    const source = sourcesMap.get(item.source_id);
    if (!source) return null;

    if (source.logo_url) return resolveMediaUrl(source.logo_url);

    if (source.type === "email_newsletter") {
      const domain = getAuthorDomain(item.author);
      if (domain) return getFaviconUrl(domain);
    }

    return null;
  }, [sourcesMap]);

  const isHiddenFilter = activeTag === "__hidden__";
  const scoreThreshold = filterConfig?.scoreThreshold ?? 50;

  const { data, loading, error, setFilters, reload } = useItems({
    page: 1,
    page_size: 24,
    tag: isHiddenFilter ? undefined : activeTag,
    exclude_tag: isHiddenFilter ? undefined : (!activeTag ? "Warnung" : undefined),
    min_score: isHiddenFilter ? 0 : (highRelevance ? scoreThreshold : undefined),
    max_score: isHiddenFilter ? 0 : undefined,
    is_read: unreadOnly ? false : undefined,
    q: searchQuery || undefined,
  });

  // Sync activeTag from sidebar (parent) to local filters
  useEffect(() => {
    const hidden = activeTag === "__hidden__";
    setFilters((f) => ({
      ...f,
      page: 1,
      tag: hidden ? undefined : activeTag,
      exclude_tag: hidden ? undefined : (!activeTag ? "Warnung" : undefined),
      min_score: hidden ? 0 : (highRelevance ? scoreThreshold : undefined),
      max_score: hidden ? 0 : undefined,
    }));
  }, [activeTag, setFilters, highRelevance, scoreThreshold]);

  const handleTagChange = useCallback((tag: string | undefined) => {
    onTagChange(tag);
  }, [onTagChange]);

  const handleUnreadChange = useCallback((unread: boolean) => {
    setUnreadOnly(unread);
    setFilters((f) => ({ ...f, page: 1, is_read: unread ? false : undefined }));
  }, [setFilters]);

  const handleHighRelevanceChange = useCallback((high: boolean) => {
    setHighRelevance(high);
    setFilters((f) => ({ ...f, page: 1, min_score: high ? scoreThreshold : undefined }));
  }, [setFilters, scoreThreshold]);

  const handleSearch = useCallback((q: string) => {
    setSearchQuery(q);
    setFilters((f) => ({ ...f, page: 1, q: q || undefined }));
  }, [setFilters]);

  const handleSpam = useCallback(() => {
    reload();
    refreshTop();
  }, [reload, refreshTop]);

  const loadMore = useCallback(() => {
    if (data && data.page * data.page_size < data.total) {
      setFilters((f) => ({ ...f, page: f.page + 1 }));
    }
  }, [data, setFilters]);

  const handleOpenDetail = useCallback((id: number) => {
    setSelectedId(id);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedId(null);
    reload();
    refreshTop();
  }, [reload, refreshTop]);

  return (
    <div className="news-portal">
      {/* Hero: Top Story */}
      {topStories[0] && (
        <HeroSection
          item={topStories[0]}
          onSelect={handleOpenDetail}
          sourceLogo={getItemLogo(topStories[0])}
        />
      )}

      {/* Top-Themen Leiste */}
      {stats && stats.tag_distribution.length > 0 && (
        <div className="top-themen-bar">
          <span className="top-themen-label">Top Themen</span>
          {stats.tag_distribution.slice(0, 7).map((t) => (
            <button
              key={t.tag}
              className={`top-themen-item${activeTag === t.tag ? " active" : ""}`}
              onClick={() => onTagChange(activeTag === t.tag ? undefined : t.tag)}
            >
              <TagBadge tag={t.tag} small />
              <span className="top-themen-count">{t.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Latest News Divider */}
      <div className="feed-divider">
        <span className="feed-divider-label">
          {isHiddenFilter ? "Ausgeblendete Artikel" : activeTag ? `${activeTag} – Nachrichten` : "Aktuelle Nachrichten"}
        </span>
        <div className="feed-divider-line" />
        {(activeTag || isHiddenFilter) && (
          <button
            className="feed-divider-clear"
            onClick={() => handleTagChange(undefined)}
            title="Filter entfernen"
          >
            ✕
          </button>
        )}
      </div>

      {/* Sticky Search & Filter */}
      <div className="feed-toolbar">
        <SearchBar
          onSearch={handleSearch}
          placeholder="Artikel durchsuchen..."
          initialValue={searchQuery}
        />
        <FilterChips
          unreadOnly={unreadOnly}
          highRelevance={highRelevance}
          onUnreadChange={handleUnreadChange}
          onHighRelevanceChange={handleHighRelevanceChange}
          resultCount={data?.total}
          showUnread={filterConfig?.showUnread ?? true}
          showScore={filterConfig?.showScore ?? true}
          scoreThreshold={scoreThreshold}
        />
      </div>

      {/* Feed */}
      {loading && !data && <p className="loading-text">Lade News...</p>}
      {error && <p className="error-text">Fehler: {error}</p>}

      {data && data.items.length === 0 && !loading && (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <p className="empty-state-title">Du bist auf dem neuesten Stand!</p>
          <p className="text-muted">Keine Artikel gefunden. Versuche andere Filter.</p>
        </div>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="news-grid">
            {data.items.map((item) => (
              <NewsCard
                key={item.id}
                item={item}
                onSpam={handleSpam}
                onSelect={handleOpenDetail}
                sourceLogo={getItemLogo(item)}
              />
            ))}
          </div>

          {data.page * data.page_size < data.total && (
            <div className="load-more">
              <button className="btn-ghost" onClick={loadMore}>
                Mehr laden ({data.total - data.page * data.page_size} weitere)
              </button>
            </div>
          )}
        </>
      )}

      {/* Article Detail Drawer */}
      {selectedId !== null && (
        <ArticleDrawer itemId={selectedId} onClose={handleCloseDetail} />
      )}
    </div>
  );
}
